import type { ParcelaPayload } from "@PN/inclusao";

export interface ParcelasValidationIssue {
  item: string;
  index: number;
  message: string;
}

export interface ParcelasValidationResult {
  isValid: boolean;
  errorsByItem: Record<string, string[]>;
  issues: ParcelasValidationIssue[];
  ruleStatuses: {
    hasBaseData: boolean;
    respectsBaseWindow: boolean;
    respectsNextParcelaLimit: boolean;
  };
}

function parseIsoDate(value?: string): Date | undefined {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return undefined;
  return new Date(year, month - 1, day);
}

function normalizeDate(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date: Date, days: number): Date {
  const d = normalizeDate(date);
  d.setDate(d.getDate() + days);
  return normalizeDate(d);
}

function getBaseDate(
  parcela: ParcelaPayload,
  parcelasBaseProtheus: Record<string, string>
): Date | undefined {
  const baseIso = parcelasBaseProtheus?.[parcela.item];
  if (baseIso) return parseIsoDate(baseIso);
  return parseIsoDate(parcela.vencimento);
}

function getMaxByNextParcela(
  parcelas: ParcelaPayload[],
  idx: number
): Date | undefined {
  const nextParcela = parcelas[idx + 1];
  if (!nextParcela) return undefined;
  const nextDate = parseIsoDate(nextParcela.vencimento);
  if (!nextDate) return undefined;
  return addDays(nextDate, -1);
}

function isParcelaManual(params: {
  parcela: ParcelaPayload | undefined;
  idx: number;
  parcelasBaseProtheus: Record<string, string>;
}): boolean {
  const { parcela, idx, parcelasBaseProtheus } = params;
  if (!parcela) return false;

  const item = (parcela.item || "").trim();
  const hasBaseDateByItem =
    !!item && typeof parcelasBaseProtheus?.[item] === "string";
  if (hasBaseDateByItem) return false;

  const baseCount = Math.max(
    Object.keys(parcelasBaseProtheus || {}).length
  );
  return idx >= baseCount;
}

export function validateParcelaDate(params: {
  parcelas: ParcelaPayload[];
  idx: number;
  selectedDate: Date;
  parcelasBaseProtheus: Record<string, string>;
  isRegraAtiva: boolean;
}): { allowed: boolean; message?: string } {
  const { parcelas, idx, selectedDate, parcelasBaseProtheus, isRegraAtiva } =
    params;
  if (!isRegraAtiva) return { allowed: true };

  const parcela = parcelas[idx];
  if (!parcela) return { allowed: true };

  const current = normalizeDate(selectedDate);
  const baseDate = getBaseDate(parcela, parcelasBaseProtheus);
  const maxByNext = getMaxByNextParcela(parcelas, idx);

  if (baseDate) {
    const oneDayBeforeBase = addDays(baseDate, -1);
    const isOneDayBefore = current.getTime() === oneDayBeforeBase.getTime();
    const isSameOrAfterBase = current.getTime() >= baseDate.getTime();

    if (!isOneDayBefore && !isSameOrAfterBase) {
      return {
        allowed: false,
        message: "Data inválida: permitido apenas D-1 da base ou datas após a base.",
      };
    }
  }

  if (maxByNext && current.getTime() > maxByNext.getTime()) {
    return {
      allowed: false,
      message: "Data inválida: deve ser no máximo D-1 da próxima parcela.",
    };
  }

  return { allowed: true };
}

export function validateAllParcelas(params: {
  parcelas: ParcelaPayload[];
  parcelasBaseProtheus: Record<string, string>;
  isRegraAtiva: boolean;
}): ParcelasValidationResult {
  const { parcelas, parcelasBaseProtheus, isRegraAtiva } = params;
  if (!isRegraAtiva) {
    return {
      isValid: true,
      errorsByItem: {},
      issues: [],
      ruleStatuses: {
        hasBaseData: true,
        respectsBaseWindow: true,
        respectsNextParcelaLimit: true,
      },
    };
  }

  const issues: ParcelasValidationIssue[] = [];
  let hasBaseData = true;
  let respectsBaseWindow = true;
  let respectsNextParcelaLimit = true;

  parcelas.forEach((parcela, idx) => {
    const isManual = isParcelaManual({
      parcela,
      idx,
      parcelasBaseProtheus,
    });
    if (isManual) {
      if (!parcela.vencimento || !parcela.vencimento.trim()) {
        issues.push({
          item: parcela.item || String(idx + 1).padStart(3, "0"),
          index: idx,
          message:
            "Data obrigatória: preencha o vencimento da parcela manual adicionada.",
        });
      }
    }

    const selected = parseIsoDate(parcela.vencimento);
    if (!selected) return;

    const baseDate = getBaseDate(parcela, parcelasBaseProtheus);
    if (!baseDate) {
      hasBaseData = false;
      return;
    }

    const maxByNext = getMaxByNextParcela(parcelas, idx);
    const result = validateParcelaDate({
      parcelas,
      idx,
      selectedDate: selected,
      parcelasBaseProtheus,
      isRegraAtiva: true,
    });

    if (!result.allowed) {
      if (result.message?.includes("base")) respectsBaseWindow = false;
      if (result.message?.includes("próxima")) respectsNextParcelaLimit = false;
      issues.push({
        item: parcela.item || String(idx + 1).padStart(3, "0"),
        index: idx,
        message: result.message || "Data inválida para a regra configurada.",
      });
      return;
    }

    if (maxByNext && selected.getTime() > maxByNext.getTime()) {
      respectsNextParcelaLimit = false;
    }

  });

  const errorsByItem: Record<string, string[]> = {};
  issues.forEach((issue) => {
    if (!errorsByItem[issue.item]) errorsByItem[issue.item] = [];
    errorsByItem[issue.item].push(issue.message);
  });

  return {
    isValid: issues.length === 0,
    errorsByItem,
    issues,
    ruleStatuses: {
      hasBaseData,
      respectsBaseWindow,
      respectsNextParcelaLimit,
    },
  };
}
